import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { EDSDataGrid } from '@gehc-eds-react/eds-datagrid';
import { EDSBadge } from '@gehc-eds-react/eds-badge';
import { EDSButton } from '@gehc-eds-react/eds-button';
import { addErrorNotification } from '../../redux/slices/notificationSlice';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { selectSources } from '../../redux/slices/s3SourcesSlice';
import { createJob, fetchJobs, selectIsCreatingJob, selectJobs, selectJobsError, selectJobsStatus } from '../../redux/slices/s3JobsSlice';
import { selectUserEmail } from '../../redux/slices/userSlice';
import CreateJobModal from '../../components/CreateJobModal/CreateJobModal';
import SpinnerComponent from '../../components/SpinnerComponent/SpinnerComponent';


const readValue = (value) => {
    if (value === null || value === undefined || value === '') return '--';
    return value;
};

const normalizeStatus = (status) => (status || '')
    .toString()
    .trim()
    .replace(/([a-z])([A-Z])/g, '$1_$2')
    .toLowerCase()
    .replace(/[\s_-]+/g, '_');

const formatStatusLabel = (status) => {
    const normalizedStatus = normalizeStatus(status);

    if (!normalizedStatus) return readValue(status);
    if (normalizedStatus === 'partial_success') return 'Partial Success';

    return normalizedStatus
        .split('_')
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
};

const isFutureDateTimeValue = (value) => {
    if (!value) return false;

    const parsedValueMs = new Date(value).getTime();

    return !Number.isNaN(parsedValueMs) && parsedValueMs > Date.now();
};

const hasCompleteDateTimeValue = (value) => {
    if (!value) return false;

    const [datePart = '', timePart = ''] = value.split('T');

    return Boolean(datePart) && /^\d{2}:\d{2}$/.test(timePart);
};

const KeyValuePair = ({ label, value }) => (
    <div
        style={{
            display: 'grid',
            gridTemplateColumns: '110px minmax(0, 1fr)',
            columnGap: '8px',
            padding: '10px 12px',
            borderBottom: '1px solid var(--eds-input-default, #ecedee)',
            alignItems: 'center'
        }}
    >
        <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--eds-text-icon-label, #55595e)' }}>{label}</div>
        <div style={{ fontSize: '14px', color: 'var(--eds-text-icon-default, #313235)', overflowWrap: 'anywhere' }}>{readValue(value)}</div>
    </div>
);

const getJobStatusBadge = (status) => {
    const normalizedStatus = normalizeStatus(status);

    if (normalizedStatus === 'complete' || normalizedStatus === 'success' || normalizedStatus === 'succeeded') {
        return { variant: 'success' };
    }

    if (normalizedStatus === 'failed' || normalizedStatus === 'error') {
        return { variant: 'alert-high' };
    }

    if (normalizedStatus === 'partial' || normalizedStatus === 'partial_success') {
        return { variant: 'alert-medium' };
    }

    if (normalizedStatus === 'running' || normalizedStatus === 'in_progress' || normalizedStatus === 'processing') {
        return { variant: 'in-progress' };
    }

    if (normalizedStatus === 'queued' || normalizedStatus === 'pending' || normalizedStatus === 'submitted') {
        return { variant: 'alert-medium' };
    }

    return { variant: 'accent-gray' };
};

const SourceDetailsPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { sourceId } = useParams();

    const sourceState = location.state?.source || null;
    const selectedContractState = location.state?.selectedContract || null;
    const dispatch = useAppDispatch();
    const availableSources = useAppSelector(selectSources);
    const jobList = useAppSelector(selectJobs);
    const jobsStatus = useAppSelector(selectJobsStatus);
    const jobsError = useAppSelector(selectJobsError);
    const isCreatingJob = useAppSelector(selectIsCreatingJob);
    const loggedInUserEmail = useAppSelector(selectUserEmail);
    const isJobsLoading = jobsStatus === 'loading';

    const source = useMemo(() => {
        if (sourceState) return sourceState;
        if (!sourceId) return null;

        return availableSources.find((candidate) => (
            candidate?.source_id === sourceId
            || candidate?.sourceId === sourceId
            || candidate?.id === sourceId
        )) || null;
    }, [availableSources, sourceId, sourceState]);

    const selectedContract = useMemo(() => {
        if (selectedContractState) return selectedContractState;

        const fallbackContractId = source?.contract_id || source?.contractId || '';

        return fallbackContractId ? { ContractID: fallbackContractId } : null;
    }, [selectedContractState, source]);

    const sourceQueryId = useMemo(
        () => source?.source_id || source?.sourceId || sourceId || '',
        [source, sourceId]
    );

    const jobGridRows = useMemo(
        () => jobList.map((job, index) => ({
            ...job,
            row_id: `${job.job_id || job.id || 'job'}-${index}`,
            job_identifier: readValue(job.job_id || job.id),
            submitted_time: readValue(job.submitted_at || job.started_at || job.created_at)
        })),
        [jobList]
    );

    const jobGridColumns = useMemo(() => ({
        row_id: { displayName: 'Row Id', isPrimary: true, isHidden: true },
        job_identifier: {
            displayName: 'Job ID',
            renderCellValue: (_value, row) => (
                <button
                    type="button"
                    onClick={() => navigate(`/job-details/${row.job_id || row.id}`, {
                        state: {
                            sourceId: sourceQueryId,
                            source,
                            selectedContract
                        }
                    })}
                    style={{
                        border: 'none',
                        background: 'none',
                        color: 'var(--eds-misc-brand, #7252BC)',
                        padding: 0,
                        margin: 0,
                        cursor: 'pointer',
                        textDecoration: 'underline'
                    }}
                >
                    {row.job_identifier}
                </button>
            )
        },
        submitted_time: {
            displayName: 'Submitted At'
        },
        status: {
            displayName: 'Status',
            renderCellValue: (value) => {
                const badge = getJobStatusBadge(value);
                return (
                    <EDSBadge
                        variant={badge.variant}
                        value={formatStatusLabel(value)}
                        label=""
                        size="medium"
                    />
                );
            }
        }
    }), [navigate, selectedContract, source, sourceQueryId]);

    useEffect(() => {
        dispatch(fetchJobs({ sourceId: sourceQueryId }));
    }, [dispatch, sourceQueryId]);

    // ----- Create Job modal state -----
    const [isCreateJobPopupOpen, setIsCreateJobPopupOpen] = useState(false);

    // 'filter' -> replicate_prefix / include_completed / created_after
    // 'prefix' -> prefixes[]
    const [jobMode, setJobMode] = useState('filter');

    const [jobAccountId, setJobAccountId] = useState('');
    const [jobReplicatePrefix, setJobReplicatePrefix] = useState('');
    const [jobKeySuffix, setJobKeySuffix] = useState('');
    const [jobCreatedAfter, setJobCreatedAfter] = useState('');
    const [jobPrefixesText, setJobPrefixesText] = useState('');
    const [jobDatasetName, setJobDatasetName] = useState('');
    const [jobDescription, setJobDescription] = useState('');
    const [jobReplicatePrefixError, setJobReplicatePrefixError] = useState('');
    const [jobPrefixesError, setJobPrefixesError] = useState('');
    const [jobDatasetNameError, setJobDatasetNameError] = useState('');

    const resolvedSourceId = source?.source_id || source?.sourceId || sourceId;
    const resolvedContractId = selectedContract?.ContractID || selectedContract?.contract_id || source?.contract_id || '';
    const hasRunTestConnection = Boolean(source?.connection_status || source?.last_tested_at);

    const goBack = () => {
        navigate('/s3-upload', { state: { selectedContract } });
    };

    const openCreateJobPopup = () => {
        // Pre-fill from the current source so the modal reflects what it's creating a job for.
        setJobMode('filter');
        setJobAccountId(source?.account_id || '');
        setJobReplicatePrefix(source?.replicate_prefix || '');
        setJobKeySuffix('');
        setJobCreatedAfter('');
        setJobPrefixesText('');
        setJobDatasetName('');
        setJobDescription('');
        setJobReplicatePrefixError('');
        setJobPrefixesError('');
        setJobDatasetNameError('');
        setIsCreateJobPopupOpen(true);
    };

    const handleCreateJobButtonClick = () => {
        if (!hasRunTestConnection) {
            dispatch(addErrorNotification({
                message: 'Run test connection for this source before creating a job.'
            }));
            return;
        }
        openCreateJobPopup();
    };

    const closeCreateJobPopup = () => {
        if (isCreatingJob) return;
        setIsCreateJobPopupOpen(false);
    };

    const parsedPrefixes = (jobPrefixesText || '')
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean);

    const saveCreateJob = async () => {
        // Prefix / Prefixes is mandatory, but which field depends on the selected mode.
        if (jobMode === 'prefix') {
            if (parsedPrefixes.length === 0) {
                setJobPrefixesError('Enter at least one prefix.');
                return;
            }
            setJobPrefixesError('');
        } else {
            if (!jobReplicatePrefix.trim()) {
                setJobReplicatePrefixError('Prefix is required.');
                return;
            }
            setJobReplicatePrefixError('');
        }

        if (!jobDatasetName.trim()) {
            setJobDatasetNameError('Dataset name is required.');
            return;
        }
        setJobDatasetNameError('');

        if (isFutureDateTimeValue(jobCreatedAfter)) {
            dispatch(addErrorNotification({
                message: 'Created After cannot be in the future.'
            }));
            return;
        }

        // Filter and Prefix modes submit different payload shapes to the create-job API.
        const payload = jobMode === 'prefix'
            ? {
                source_id: resolvedSourceId,
                contract_id: resolvedContractId,
                dataset_name: jobDatasetName.trim(),
                created_after: hasCompleteDateTimeValue(jobCreatedAfter) ? new Date(jobCreatedAfter).toISOString() : null,
                description: jobDescription,
                email: loggedInUserEmail || null,
                prefixes: parsedPrefixes
            }
            : {
                source_id: resolvedSourceId,
                contract_id: resolvedContractId,
                dataset_name: jobDatasetName.trim(),
                replicate_prefix: jobReplicatePrefix,
                key_suffix: jobKeySuffix,
                // datetime-local gives "2026-07-06T15:00", convert to ISO with seconds/Z
                created_after: hasCompleteDateTimeValue(jobCreatedAfter) ? new Date(jobCreatedAfter).toISOString() : null,
                description: jobDescription,
                email: loggedInUserEmail || null
            };

        try {
            await dispatch(createJob({ payload, sourceId: resolvedSourceId })).unwrap();
            setIsCreateJobPopupOpen(false);
        } catch {
            // Error notification is dispatched by the thunk.
        }
    };

    return (
        <div className="data-upload-container">
            <SpinnerComponent isVisible={isCreatingJob} overlay={true} message="Creating job..." />
            <div className="upload-data-header fill--1">
                <h1 className='u-mt0 u-mb0'>Source Details</h1>
            </div>

            <div className="flex user-groups-container">
                <div className="user-details-section u--1/4 fill--1" style={{ paddingBottom: '24px' }}>
                    <div>
                        <h4 className='u-mt0 u-mb--'>Source</h4>
                    </div>

                    <div
                        className='u-mt'
                        style={{
                            backgroundColor: '#fff',
                            border: '1px solid var(--eds-input-default, #ecedee)',
                            borderRadius: '12px',
                            overflow: 'hidden'
                        }}
                    >
                        <KeyValuePair label="Source ID" value={resolvedSourceId} />
                        <KeyValuePair label="Account Number" value={source?.account_id} />
                        <KeyValuePair label="Bucket Name" value={source?.source_bucket} />
                        <KeyValuePair label="Prefix" value={source?.replicate_prefix} />
                        <KeyValuePair label="Display Name" value={source?.display_name} />
                    </div>
                </div>

                <div
                    className="u--3/4 fill--1"
                    style={{
                        padding: '12px 24px 0 24px',
                        height: '100%',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: 0
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                        <h4 className='u-mt0 u-mb--'>Job List</h4>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
                            <EDSButton
                                variant="secondary"
                                type="button"
                                label="Back"
                                onEDSButtonClick={goBack}
                            />
                            <EDSButton
                                variant="primary"
                                type="button"
                                label="Create Job"
                                onEDSButtonClick={handleCreateJobButtonClick}
                            />
                        </div>
                    </div>

                    <div
                        style={{
                            marginTop: '12px',
                            flex: '1 1 auto',
                            minHeight: 0,
                            overflow: 'auto'
                        }}
                    >
                        {isJobsLoading && (
                            <div style={{ padding: '12px 8px', color: '#666' }}>
                                Loading jobs...
                            </div>
                        )}

                        {!isJobsLoading && jobsError && (
                            <div style={{ padding: '12px 8px', color: '#d32f2f' }}>
                                {jobsError}
                            </div>
                        )}

                        {!isJobsLoading && !jobsError && jobList.length === 0 && (
                            <div style={{ padding: '12px 8px', color: '#666' }}>
                                No jobs found for this source.
                            </div>
                        )}

                        {!isJobsLoading && !jobsError && jobGridRows.length > 0 && (
                            <EDSDataGrid
                                columnConfig={jobGridColumns}
                                rows={jobGridRows}
                                gridFeatures={["row-hover", "table-flex-horizontal"]}
                                rowFormat={{ size: 'compact', style: 'divider' }}
                                labels={{
                                    rowText: 'rows of',
                                    noRowsFoundText: 'No jobs found for this source.'
                                }}
                                visibleRowCount={Math.max(1, jobGridRows.length)}
                                totalRowCount={jobGridRows.length}
                                activeRowId=""
                            />
                        )}
                    </div>
                </div>
            </div>
            <CreateJobModal
                isOpen={isCreateJobPopupOpen}
                isCreatingJob={isCreatingJob}
                resolvedSourceId={resolvedSourceId}
                jobAccountId={jobAccountId}
                jobMode={jobMode}
                setJobMode={setJobMode}
                jobReplicatePrefix={jobReplicatePrefix}
                setJobReplicatePrefix={setJobReplicatePrefix}
                jobReplicatePrefixError={jobReplicatePrefixError}
                setJobReplicatePrefixError={setJobReplicatePrefixError}
                jobKeySuffix={jobKeySuffix}
                setJobKeySuffix={setJobKeySuffix}
                jobCreatedAfter={jobCreatedAfter}
                setJobCreatedAfter={setJobCreatedAfter}
                jobPrefixesText={jobPrefixesText}
                setJobPrefixesText={setJobPrefixesText}
                jobPrefixesError={jobPrefixesError}
                setJobPrefixesError={setJobPrefixesError}
                jobDatasetName={jobDatasetName}
                setJobDatasetName={setJobDatasetName}
                jobDatasetNameError={jobDatasetNameError}
                setJobDatasetNameError={setJobDatasetNameError}
                jobDescription={jobDescription}
                setJobDescription={setJobDescription}
                onClose={closeCreateJobPopup}
                onSave={saveCreateJob}
            />
        </div>
    );
};

export default SourceDetailsPage;